// Frontmatter panel for the document detail page.
//
// 2026-06-03 cleanup (drthe0):
//  - Wraps in CollapsiblePanel so the section persists open/closed.
//  - Nested object values render as indented sub-rows in a smaller,
//    muted secondary font instead of a loud JSON <pre> block. Quieter,
//    less distracting, easier to scan.
//  - The "show more" / size hints are now lighter-weight (text link,
//    no bold treatment) so they read as secondary chrome.
//  - Long string values still get truncate + expand; objects still
//    fall back to pre-formatted JSON when they're too large to render
//    inline cleanly (more than one nested level deep, or any array).

import { useMemo, useState } from 'react';
import { CollapsiblePanel } from './CollapsiblePanel.js';

interface FrontmatterPanelProps {
  data: Record<string, unknown>;
}

/** Keys whose values are known to dominate the panel and aren't useful
 *  inline. The operator can still expand them; they just don't autoplay. */
const COLLAPSED_BY_DEFAULT = new Set([
  '_content_plain',
  '_content_html',
  '_content',
  'content',
  'body',
  'raw',
  'html',
]);

/** Length above which a string value gets truncated. Chosen so a typical
 *  summary fits but anything paragraph-sized triggers the toggle. */
const TRUNCATE_AT = 240;

/** Max nested keys to render inline before falling back to JSON. Keeps
 *  the inline render readable; deep nesting becomes a JSON code block. */
const NESTED_INLINE_LIMIT = 16;

/** Sort priority: known meta first, then alphabetical, then noisy/large last. */
function compareKeys(a: string, b: string): number {
  const aNoisy = COLLAPSED_BY_DEFAULT.has(a) ? 1 : 0;
  const bNoisy = COLLAPSED_BY_DEFAULT.has(b) ? 1 : 0;
  if (aNoisy !== bNoisy) return aNoisy - bNoisy;
  return a.localeCompare(b);
}

export function FrontmatterPanel({ data }: FrontmatterPanelProps) {
  const entries = useMemo(
    () => Object.entries(data).sort(([a], [b]) => compareKeys(a, b)),
    [data],
  );

  if (entries.length === 0) return null;

  return (
    <CollapsiblePanel
      storageKey="docdetail:frontmatter"
      title="Frontmatter"
      badge={`${entries.length} field${entries.length === 1 ? '' : 's'}`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map(([k, v]) => <FrontmatterRow key={k} k={k} v={v} />)}
      </div>
    </CollapsiblePanel>
  );
}

/** Detects an inline-renderable nested object: plain object whose total
 *  key count is small AND every value is a primitive. Arrays, nested
 *  objects-in-objects, and large maps fall through to JSON rendering. */
function isInlineRenderable(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const obj = v as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length === 0 || keys.length > NESTED_INLINE_LIMIT) return false;
  for (const key of keys) {
    const value = obj[key];
    if (value !== null && typeof value === 'object') return false;
  }
  return true;
}

function formatPrimitive(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  return JSON.stringify(v);
}

function FrontmatterRow({ k, v }: { k: string; v: unknown }) {
  const isObject = v !== null && typeof v === 'object';
  const isString = typeof v === 'string';
  const stringValue = isString ? v : (isObject ? JSON.stringify(v, null, 2) : String(v));
  const isLong = stringValue.length > TRUNCATE_AT || stringValue.includes('\n');
  const isNoisy = COLLAPSED_BY_DEFAULT.has(k);
  const inlineNested = isObject && isInlineRenderable(v);

  // Long + noisy values default-collapsed; inline-nested objects
  // default-collapsed too (they're rich enough to merit deliberate
  // expansion); everything else open.
  const [expanded, setExpanded] = useState(
    !(isLong && (isNoisy || isObject)) && !inlineNested,
  );

  // ─── Inline-renderable nested object (the new clean style) ────
  if (inlineNested) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <KeyLabel k={k} />
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            style={secondaryButtonStyle}
          >
            {expanded ? 'hide' : `${Object.keys(v as Record<string, unknown>).length} fields`}
          </button>
        </div>
        {expanded && (
          <div
            style={{
              marginLeft: 12,
              paddingLeft: 8,
              borderLeft: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {Object.entries(v as Record<string, unknown>)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([nk, nv]) => (
                <div
                  key={nk}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr',
                    gap: 6,
                    alignItems: 'baseline',
                    fontSize: 11,
                  }}
                >
                  <span
                    style={{
                      color: 'var(--text-muted)',
                      fontFamily: 'var(--font-mono)',
                      opacity: 0.85,
                      wordBreak: 'break-word',
                    }}
                  >
                    {nk}
                  </span>
                  <span
                    style={{
                      color: 'var(--text-secondary, var(--text-primary))',
                      opacity: 0.85,
                      wordBreak: 'break-word',
                      fontFamily: typeof nv === 'string' ? undefined : 'var(--font-mono)',
                    }}
                  >
                    {formatPrimitive(nv) || <em style={{ opacity: 0.55 }}>(empty)</em>}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Short inline value ───────────────────────────────────────
  if (!isLong) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'baseline' }}>
        <KeyLabel k={k} />
        <span
          style={{
            fontFamily: isObject || isString ? 'var(--font-mono)' : undefined,
            fontSize: 12,
            color: 'var(--text-primary)',
            wordBreak: 'break-word',
          }}
        >
          {stringValue || <em style={{ color: 'var(--text-muted)', opacity: 0.6 }}>(empty)</em>}
        </span>
      </div>
    );
  }

  // ─── Long / multiline / complex-object — collapsible block ────
  const preview = stringValue.slice(0, TRUNCATE_AT).replace(/\s+/g, ' ').trim();
  const lineCount = stringValue.split('\n').length;
  const charCount = stringValue.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <KeyLabel k={k} />
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          style={secondaryButtonStyle}
        >
          {expanded ? 'hide' : `${formatSize(lineCount, charCount)}`}
        </button>
      </div>
      {!expanded && (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--text-muted)',
            opacity: 0.7,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            paddingLeft: 12,
          }}
          title={preview}
        >
          {preview}…
        </div>
      )}
      {expanded && (
        <pre
          style={{
            margin: 0,
            padding: 8,
            background: 'var(--surface-canvas)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 4,
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            lineHeight: 1.45,
            color: 'var(--text-secondary, var(--text-primary))',
            opacity: 0.9,
            maxHeight: 320,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {stringValue}
        </pre>
      )}
    </div>
  );
}

const secondaryButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-muted)',
  fontSize: 10,
  cursor: 'pointer',
  padding: '0 4px',
  opacity: 0.75,
};

function KeyLabel({ k }: { k: string }) {
  return (
    <span
      style={{
        fontSize: 12,
        color: 'var(--text-muted)',
        fontFamily: 'var(--font-mono)',
        wordBreak: 'break-word',
      }}
    >
      {k}
    </span>
  );
}

function formatSize(lines: number, chars: number): string {
  if (lines > 5) return `${lines} lines`;
  if (chars > 1024) return `${(chars / 1024).toFixed(1)} KB`;
  return `${chars} chars`;
}
